import type { ReactNode, Ref } from "react";
import { IconNameEnum, useModuleBEM } from "@b2bportal/core-themes";
import { useI18nContext } from "@hopper-b2b/i18n";
import { Icon, SkeletonLoading } from "@tenant/ui";
import clsx from "clsx";
import dayjs from "dayjs";
import styles from "./DealTile.module.scss";

export interface DealTileAction {
  label: string;
  onClick: () => void;
  icon?: ReactNode;
  disabled?: boolean;
}

export interface DealTileProps {
  imageSrc: string;
  imageAlt?: string;
  title: string;
  description: string;
  /** "More info" link under the description (e.g. opens the offer dialog). */
  onMoreInfo?: () => void;
  /** ISO date; renders the "N days" countdown. Omitted → no countdown row. */
  expiry?: string;
  /** Footer status on the left, e.g. "Loaded ✓". */
  status?: string;
  /** Footer text link on the right. */
  action: DealTileAction;
  /** Pointer-only click on the card body; links never bubble to it. */
  onCardClick?: () => void;
  error?: string;
  rootRef?: Ref<HTMLDivElement>;
  className?: string;
}

/** Whole days left until `expiry`, floored at 0. */
export const getDaysUntilExpiry = (expiry: string) =>
  Math.max(0, dayjs(expiry).startOf("day").diff(dayjs().startOf("day"), "day"));

/**
 * Travel deal card shared by funded hotel deals and HTS Media sponsored offers
 * (Figma 40548-40221). Width is left to the container.
 */
export const DealTile = ({
  imageSrc,
  imageAlt = "",
  title,
  description,
  onMoreInfo,
  expiry,
  status,
  action,
  onCardClick,
  error,
  rootRef,
  className,
}: DealTileProps) => {
  const { t } = useI18nContext();
  const [block, cn] = useModuleBEM(styles, "DealTile", {
    clickable: onCardClick != null,
  });

  return (
    // The footer actions are the keyboard-accessible controls; the card click
    // is a pointer-only convenience, hence no key handler here.
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div ref={rootRef} className={clsx(block, className)} onClick={onCardClick}>
      <div className={cn("image-container")}>
        <img className={cn("image")} src={imageSrc} alt={imageAlt} />
      </div>
      <div className={cn("body")}>
        <h3 className={cn("title")}>{title}</h3>
        <p className={cn("description")}>{description}</p>
        {onMoreInfo != null && (
          <button
            type="button"
            className={cn("more-info")}
            onClick={(event) => {
              event.stopPropagation();
              onMoreInfo();
            }}
          >
            {t("dealTile.moreInfo")}
          </button>
        )}
      </div>
      {expiry != null && (
        <div className={cn("meta")}>
          <span className={cn("expiry")}>
            <Icon iconName={IconNameEnum.clock} aria-hidden />
            {t("dealTile.daysLeft", { count: getDaysUntilExpiry(expiry) })}
          </span>
        </div>
      )}
      <div className={cn("footer")}>
        {status != null && (
          <span className={cn("status")}>
            {status}
            <Icon iconName={IconNameEnum.checkFilled} aria-hidden />
          </span>
        )}
        <button
          type="button"
          className={cn("action")}
          onClick={(event) => {
            event.stopPropagation();
            action.onClick();
          }}
          disabled={action.disabled}
          aria-busy={action.disabled}
        >
          {action.label}
          {action.icon}
        </button>
      </div>
      {error != null && (
        <p className={cn("error")} role="alert">
          {error}
        </p>
      )}
    </div>
  );
};

export const DealTileSkeleton = ({ className }: { className?: string }) => {
  const [block, cn] = useModuleBEM(styles, "DealTile");

  return (
    <div className={clsx(block, className)}>
      <div className={cn("image-container")}>
        <SkeletonLoading className={cn("skeleton-image")} />
        <div className={cn("skeleton-image-icon")} />
      </div>
      <div className={cn("body")}>
        <SkeletonLoading className={cn("skeleton-title")} />
        <SkeletonLoading className={cn("skeleton-text")} />
        <SkeletonLoading className={cn("skeleton-text")} />
      </div>
      <div className={cn("footer")}>
        <SkeletonLoading className={cn("skeleton-cta")} />
        <SkeletonLoading className={cn("skeleton-cta")} />
      </div>
    </div>
  );
};
